import Button from '@ui/Button'
import Field from '@ui/Field'
import Input from '@ui/Input'
import { type FormEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import {
  checkPasswordResetCode,
  completePasswordReset,
  IdpError,
  type IdpErrorCode,
} from '@/auth/idp'

import LoginCard from './LoginCard'

// Landing page for the reset link in our email.
const PasswordReset = () => {
  const { t } = useTranslation()
  const [params] = useSearchParams()
  const oobCode = params.get('oobCode')
  const [email, setEmail] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<IdpErrorCode | null>(
    oobCode ? null : 'link-invalid'
  )

  useEffect(() => {
    if (!oobCode) return undefined
    let live = true
    checkPasswordResetCode(oobCode)
      .then((address) => live && setEmail(address))
      .catch((err) => {
        if (live) setError(err instanceof IdpError ? err.code : 'unknown')
      })
    return () => {
      live = false
    }
  }, [oobCode])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!oobCode) return
    setBusy(true)
    setError(null)
    try {
      await completePasswordReset(oobCode, password)
      setDone(true)
    } catch (err) {
      setError(err instanceof IdpError ? err.code : 'unknown')
    } finally {
      setBusy(false)
    }
  }

  const backToSignIn = (
    <Button fullWidth onClick={() => window.location.assign('/login')}>
      {t('auth.password.back-to-sign-in')}
    </Button>
  )

  const errorLine = error && (
    <p role="alert" className="text-sm text-flag">
      {t(`auth.password.errors.${error}` as const)}
    </p>
  )

  if (done) {
    return (
      <LoginCard title={t('auth.password-reset.title')}>
        <p className="text-sm text-ink-700">{t('auth.password-reset.done')}</p>
        {backToSignIn}
      </LoginCard>
    )
  }

  if (!email) {
    return (
      <LoginCard title={t('auth.password-reset.title')}>
        {error ? (
          <>
            {errorLine}
            {backToSignIn}
          </>
        ) : (
          <p className="text-sm text-ink-500">{t('common.loading')}</p>
        )}
      </LoginCard>
    )
  }

  return (
    <LoginCard
      title={t('auth.password-reset.title')}
      subtitle={t('auth.password-reset.for', { email })}
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field
          label={t('auth.password-reset.new-password')}
          hint={t('auth.password.password-hint')}
        >
          <Input
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            required
          />
        </Field>
        {errorLine}
        <Button type="submit" fullWidth disabled={busy}>
          {t('auth.password-reset.save')}
        </Button>
      </form>
    </LoginCard>
  )
}

export default PasswordReset
